// src/components/aiTalentDrive/CandidateRatings.tsx
// "Candidate Ratings" on /ai-talent-drive: the 4 and 5 star ratings drive
// candidates gave their AI interview, as a centre-mode carousel. Read-only:
// it GETs one fixed list (babblebots-monolith companyapp/hiring_circuit.py
// public_ratings) and draws it. Polls every minute while the tab is visible
// so a fresh rating joins the deck without a reload; renders nothing until
// there are at least three cards.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Slider, { Settings } from "react-slick";
import { CheckIcon, StarIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import "slick-carousel/slick/slick.css";

export interface CandidateRating {
  first_name: string;
  track: string;
  rating: number;
  reasons: string[];
}

interface RatingsResponse {
  average: number | null;
  rated_count: number;
  ratings: CandidateRating[];
}

const POLL_MS = 60_000;
const MIN_CARDS = 3;
const AUTOPLAY_MS = 6_000;

// The API carries no ids by design, so a card is identified by its content.
const keyOf = (r: CandidateRating) =>
  [r.first_name, r.track, r.rating, ...r.reasons].join("\u001f");

const isRating = (value: unknown): value is CandidateRating => {
  if (!value || typeof value !== "object") return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.first_name === "string" &&
    typeof r.track === "string" &&
    typeof r.rating === "number" &&
    r.rating >= 4 &&
    r.rating <= 5 &&
    Array.isArray(r.reasons) &&
    r.reasons.every((x) => typeof x === "string")
  );
};

// Same pill twice in one rating is noise: keep the first occurrence.
const dedupe = (reasons: string[]) => Array.from(new Set(reasons));

const Stars = ({ rating }: { rating: number }) => (
  <span className="cr-stars" role="img" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((star) => (
      <StarIcon
        key={star}
        size={17}
        weight="fill"
        className={star <= rating ? "cr-star on" : "cr-star"}
      />
    ))}
  </span>
);

// react-slick clones custom arrows with currentSlide/slideCount props,
// which must not reach the DOM button.
interface ArrowProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  currentSlide?: number;
  slideCount?: number;
  className?: string;
  style?: React.CSSProperties;
}

const PrevArrow = ({ onClick }: ArrowProps) => (
  <button type="button" className="cr-arrow cr-arrow-left" onClick={onClick} aria-label="Previous rating">
    <CaretLeftIcon size={20} weight="bold" />
  </button>
);

const NextArrow = ({ onClick }: ArrowProps) => (
  <button type="button" className="cr-arrow cr-arrow-right" onClick={onClick} aria-label="Next rating">
    <CaretRightIcon size={20} weight="bold" />
  </button>
);

// Side cards show two reasons plus a "+N more" line; the centre card shows
// all of them.
const SIDE_REASONS = 2;

// A 4 or 5 star rating given without tapping any pill arrives with an empty
// list; the card shows these two stock reasons for that star instead.
const DEFAULT_REASONS: Record<number, string[]> = {
  5: ["Felt like a natural conversation", "Comfortable and engaging experience"],
  4: ["Questions were relevant", "Conversation flowed well"],
};
const reasonsOf = (rating: CandidateRating) =>
  rating.reasons.length ? rating.reasons : DEFAULT_REASONS[rating.rating] ?? [];

const Card = ({ rating, isCenter }: { rating: CandidateRating; isCenter: boolean }) => {
  const reasons = reasonsOf(rating);
  const shown = isCenter ? reasons : reasons.slice(0, SIDE_REASONS);
  const hidden = reasons.length - shown.length;
  return (
    <article className="cr-card">
      <div className="cr-card-top">
        <Stars rating={rating.rating} />
      </div>
      <ul className="cr-reasons">
        {shown.map((reason) => (
          <li key={reason}>
            <CheckIcon size={20} weight="bold" aria-hidden="true" />
            {reason}
          </li>
        ))}
        {hidden > 0 && (
          <li className="cr-more mono" aria-label={`${hidden} more reasons`}>
            +{hidden} more
          </li>
        )}
      </ul>
      <div className="cr-who">
        <span className="cr-avatar" aria-hidden="true">
          {rating.first_name.charAt(0)}
        </span>
        <div>
          <div className="cr-name">{rating.first_name}</div>
          <div className="cr-role mono">{rating.track}</div>
        </div>
      </div>
    </article>
  );
};

interface Props {
  apiBase: string;
}

export default function CandidateRatings({ apiBase }: Props) {
  const [ratings, setRatings] = useState<CandidateRating[] | null>(null);
  const [current, setCurrent] = useState(0);
  const sliderRef = useRef<Slider | null>(null);
  const knownKeys = useRef<Set<string>>(new Set());
  const currentRef = useRef(0);

  // react-slick's own idea of the centre slide. afterChange alone is not
  // enough: a re-init (children changed, window resized) resets the slider
  // without firing it, and the highlight would drift one card off.
  const slickCurrent = () => {
    const inner = (sliderRef.current as unknown as { innerSlider?: { state?: { currentSlide?: number } } } | null)?.innerSlider;
    const index = inner?.state?.currentSlide;
    return typeof index === "number" ? index : currentRef.current;
  };
  const syncCurrent = useCallback(() => {
    const index = slickCurrent();
    currentRef.current = index;
    setCurrent(index);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/hiring-circuit/ratings/`);
      if (!res.ok) return;
      const body = (await res.json()) as RatingsResponse;
      const incoming = (Array.isArray(body?.ratings) ? body.ratings : [])
        .filter(isRating)
        .map((r) => ({ ...r, reasons: dedupe(r.reasons) }));

      setRatings((previous) => {
        if (previous === null) {
          knownKeys.current = new Set(incoming.map(keyOf));
          return incoming;
        }
        // Keep the deck the visitor is looking at; only prepend cards we
        // have not shown before, then nudge the slider so the same card
        // stays in the middle.
        const fresh = incoming.filter((r) => !knownKeys.current.has(keyOf(r)));
        if (!fresh.length) return previous;
        fresh.forEach((r) => knownKeys.current.add(keyOf(r)));
        const next = [...fresh, ...previous];
        const target = slickCurrent() + fresh.length;
        window.requestAnimationFrame(() => {
          sliderRef.current?.slickGoTo(target, true);
          window.setTimeout(syncCurrent, 0);
        });
        return next;
      });
    } catch {
      // A failed refresh keeps what is on screen; the first failure simply
      // leaves the section unrendered.
    }
  }, [apiBase, syncCurrent]);

  useEffect(() => {
    load();
    const tick = () => {
      if (document.visibilityState === "visible") load();
    };
    const timer = window.setInterval(tick, POLL_MS);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", tick);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [load]);

  const reducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const count = ratings?.length ?? 0;

  const settings: Settings = useMemo(
    () => ({
      centerMode: true,
      centerPadding: "0px",
      slidesToShow: 3,
      slidesToScroll: 1,
      infinite: count > 3,
      // Smooth decelerating slide; the centre card then settles (CSS).
      speed: 600,
      cssEase: "cubic-bezier(.22, 1, .36, 1)",
      autoplay: !reducedMotion,
      autoplaySpeed: AUTOPLAY_MS,
      pauseOnHover: true,
      pauseOnFocus: true,
      swipeToSlide: true,
      arrows: true,
      dots: true,
      prevArrow: <PrevArrow />,
      nextArrow: <NextArrow />,
      dotsClass: "slick-dots cr-dots",
      afterChange: (index: number) => {
        currentRef.current = index;
        setCurrent(index);
      },
      onInit: syncCurrent,
      onReInit: syncCurrent,
      responsive: [
        {
          breakpoint: 860,
          settings: { slidesToShow: 1, centerPadding: "11%", arrows: false },
        },
      ],
    }),
    [count, reducedMotion, syncCurrent]
  );

  if (!ratings || count < MIN_CARDS) return null;

  // Distance from the centre slide, with wrap-around, drives the size and
  // fade of each card so the middle one is always the highlighted one.
  const distance = (index: number) => {
    const raw = Math.abs(index - current);
    return Math.min(raw, count - raw);
  };

  return (
    <section className="cr-section" aria-labelledby="cr-title">
      {/* No hc-fade here: the page's reveal observer registers elements at
          first render, and this section only mounts once its data loads. */}
      <div className="section-head section-head--center">
        <h2 id="cr-title">What candidates say</h2>
      </div>
      <div className="cr-carousel">
        <Slider ref={sliderRef} {...settings}>
          {ratings.map((rating, index) => (
            <div key={keyOf(rating)} className={`cr-slide cr-d${Math.min(distance(index), 2)}`}>
              <Card rating={rating} isCenter={distance(index) === 0} />
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
}
